import { format } from 'date-fns';
import { subYears } from 'date-fns/subYears';
import {
  I18nService,
  WindowService,
} from '@gen-epix/ui';
import type { CaseDbConfig } from '@gen-epix/ui-casedb';
import { CaseDbStandardConfigUtil } from '@gen-epix/ui-casedb';
import { t } from 'i18next';

import { createTheme } from '../../theme';
import { ApplicationHeader } from '../../components/ApplicationHeader';
import { ConsentDialogContent } from '../../components/ConsentDialogContent';
import { HomePageIntroduction } from '../../components/HomePageIntroduction';
import { LicenseInformation } from '../../components/LicenseInformation';
import { LoginMockLoader } from '../../components/LoginMockLoader';
import { NonceUtil } from '../NonceUtil/NonceUtil';
import { EnvironmentSettingsService } from '../../services/EnvironmentSettingsService';

const onEnglishClick = () => {
  I18nService.getInstance().emit('onUserLanguageChange', 'en');
};

const onDutchClick = () => {
  I18nService.getInstance().emit('onUserLanguageChange', 'nl');
};

export class ConfigUtil {
  public static createConfig(): CaseDbConfig {
    const config: CaseDbConfig = {
      ...CaseDbStandardConfigUtil.createConfig(),

      // eslint-disable-next-line @typescript-eslint/naming-convention
      ApplicationHeader,
      applicationName: 'LabSentiNL',
      consentDialog: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Content: ConsentDialogContent,
        getButtonLabel: () => t`I agree`,
        getShouldShow: () => {
          if (import.meta.env.DEV) {
            return false; // In development mode, we don't show the consent dialog
          }
          if (WindowService.getInstance().window.sessionStorage.getItem('consent-dialog-accepted-by-playwright')) {
            return false; // If the consent dialog was accepted by Playwright, we don't show it again
          }
          return true;
        },
        getTitle: () => t`TERMS OF USE`,
      },
      createFooter: () => ({
        sections: [
          {
            header: t`Contact`,
            items: [
              {
                href: 'mailto:ids-bioinformatics@rivm.nl',
                label: t`ids-bioinformatics@rivm.nl`,
              },
              {
                href: 'https://www.rivm.nl/contact/persinformatie',
                label: t`Information for the press`,
              },
            ],
          },
          {
            header: t`About`,
            items: [
              {
                href: 'https://www.rivm.nl/copyright',
                label: t`Copyright`,
              },
              {
                href: 'https://www.rivm.nl/toegankelijkheid',
                label: t`Accessibility`,
              },
            ],
          },
          {
            header: t`Languages`,
            items: [
              {
                label: t`English`,
                onClick: onEnglishClick,
              },
              {
                label: t`Dutch`,
                onClick: onDutchClick,
              },
            ],
          },
        ],
      }),
      enablePageEvents: true,
      getAPIBaseUrl: () => {
        return EnvironmentSettingsService.getInstance().settings.API_BASE_URL;
      },
      getEnvironmentMessage: () => {
        return EnvironmentSettingsService.getInstance().settings.ENVIRONMENT_MESSAGE_BASE;
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      getSoftwareVersion: () => import.meta.env.VITE_RELEASED_VERSION as string ?? `${__PACKAGE_JSON_VERSION__}-snapshot-${__COMMIT_HASH__}`,
      getTouchIconUrl: () => {
        return EnvironmentSettingsService.getInstance().settings.TOUCH_ICON_PATH ?? '/touch-icon.png';
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      HomePageIntroduction,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      LicenseInformation,
      login: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        AfterIdentityProviderSelection: import.meta.env.DEV ? LoginMockLoader : undefined,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        AfterLoginElement: import.meta.env.DEV ? LoginMockLoader : undefined,
      },
      nonce: NonceUtil.getNonce(),
      theme: createTheme('light'),
      trends: {
        homePage: {
          getSinceDate: () => format(subYears(new Date().toISOString(), 1), 'yyyy-MM-dd'),
          getSinceLabel: () => t`since last year`,
        },
      },
    };
    return config;
  }
}
